import React from 'react';
import PublicLayout from '../../components/PublicLayout';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <PublicLayout>
      <div className="privacy-container">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p><strong>Last Updated:</strong> 14th Jul 2025</p>
        </div>
        
        <div className="privacy-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Oslith AI Private Limited, operating the Velox AI platform ("we," "us," or "our") is committed to protecting the privacy and security of your personal information. This Privacy Policy describes how we collect, use, process, and disclose information when you use our B2B revenue operations intelligence platform and related services (collectively, the "Service").
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, phone number, job title, company name, and billing information</li>
              <li><strong>Profile Data:</strong> Professional information, preferences, and settings</li>
              <li><strong>Content Data:</strong> Sales data, customer information, documents, and other content you upload or create</li>
              <li><strong>Communication Data:</strong> Messages, feedback, and support requests</li>
            </ul>

            <h3>2.2 Information We Collect Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> How you interact with our Service, features used, and performance metrics</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
              <li><strong>Log Data:</strong> Access times, pages viewed, and system activity</li>
              <li><strong>Cookies and Tracking:</strong> We use cookies and similar technologies to enhance functionality</li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <ul>
              <li><strong>Integration Data:</strong> Information from CRM systems, email platforms, and other business tools you connect</li>
              <li><strong>Public Sources:</strong> Publicly available business information to enhance our services</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and manage your account</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send important notifications and updates</li>
              <li>Analyze usage patterns and optimize performance</li>
              <li>Develop new features and services</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraud and security threats</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            
            <h3>4.1 We Share Information With:</h3>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors who assist in providing our Service</li>
              <li><strong>Business Partners:</strong> Authorized integrations and connected applications</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or regulatory authority</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
              <li><strong>Consent:</strong> When you explicitly authorize sharing</li>
            </ul>

            <h3>4.2 We Do Not:</h3>
            <ul>
              <li>Sell your personal information to third parties</li>
              <li>Share your confidential business data without authorization</li>
              <li>Use your data for competitive purposes</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>We implement industry-standard security measures including:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Access controls and authentication systems</li>
              <li>Regular security assessments and monitoring</li>
              <li>Secure data centers and infrastructure</li>
              <li>Employee training on data protection</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>We retain your information for as long as:</p>
            <ul>
              <li>Your account remains active</li>
              <li>Required to provide our Service</li>
              <li>Necessary for legal compliance</li>
              <li>Required for legitimate business purposes</li>
            </ul>
            <p>You may request deletion of your account and associated data at any time, subject to legal retention requirements.</p>
          </section>

          <section>
            <h2>7. Your Rights and Choices</h2>
            
            <h3>7.1 Access and Control</h3>
            <ul>
              <li>Access, update, or delete your personal information</li>
              <li>Export your data in a portable format</li>
              <li>Control privacy settings and preferences</li>
              <li>Opt-out of marketing communications</li>
            </ul>

            <h3>7.2 Regional Rights</h3>
            
            <p><strong>For EU/UK Users (GDPR/UK GDPR):</strong></p>
            <ul>
              <li>Right to access, rectify, and erase personal data</li>
              <li>Right to data portability and restriction of processing</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>

            <p><strong>For California Users (CCPA):</strong></p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of sale of personal information</li>
              <li>Right to non-discrimination</li>
            </ul>
          </section>

          <section>
            <h2>8. International Data Transfers</h2>
            <p>
              <strong>Data Processing Location:</strong> Oslith AI Private Limited, operating the Velox AI platform, is based in India, and we may process and store your data in India or other countries where we or our service providers operate. We ensure appropriate safeguards are in place for all international transfers:
            </p>
            <ul>
              <li><strong>Standard Contractual Clauses (SCCs)</strong> approved by the European Commission</li>
              <li><strong>Adequacy decisions</strong> where applicable</li>
              <li><strong>Binding Corporate Rules</strong> for intra-group transfers</li>
              <li><strong>Other approved transfer mechanisms</strong> as required by applicable law</li>
            </ul>
            <p>
              <strong>Cross-Border Data Flow:</strong> By using our Service, you acknowledge and consent to the transfer of your personal data to India and other jurisdictions as necessary for service provision.
            </p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>Our Service is not intended for individuals under 16 years of age. We do not knowingly collect personal information from children under 16.</p>
          </section>

          <section>
            <h2>10. Third-Party Links and Services</h2>
            <p>Our Service may contain links to third-party websites or integrate with third-party services. We are not responsible for their privacy practices.</p>
          </section>

          <section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy periodically. We will notify you of material changes via email or through our Service. Your continued use constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2>12. Contact Information</h2>
            <p>For privacy-related questions or concerns, contact us at:</p>
            <p><strong>Email:</strong> velox-ai@outlook.com</p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}

export default PrivacyPolicy; 